/** White-label branding: a partner's own domain shows the partner's name, logo and colour. */
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { BRAND } from "./brand";

export interface Brand {
  name: string; support_email: string; color: string; logo_url: string | null; icon_url: string | null;
  powered_by: boolean; partner: string | null;
}

export const DEFAULT_BRAND: Brand = {
  name: BRAND.name, support_email: "", color: "#7c4ef0", logo_url: null, icon_url: null, powered_by: true, partner: null,
};

async function fetchBrand(): Promise<Brand> {
  const res = await fetch(`/api/v1/public/branding?host=${encodeURIComponent(window.location.host)}`);
  return res.ok ? ((await res.json()) as Brand) : DEFAULT_BRAND;
}

/** The brand for this address. Our own until the answer arrives (and if it fails). */
export function useBrand(): Brand & { icon: string } {
  const { data } = useQuery({ queryKey: ["branding", window.location.host], queryFn: fetchBrand, staleTime: Infinity, gcTime: Infinity, retry: 1 });
  const brand = data ?? DEFAULT_BRAND;
  return { ...brand, icon: brand.icon_url ?? (brand.partner ? "" : BRAND.icon) };
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const toHex = (rgb: number[]) => `#${rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("")}`;
/** Mix toward black (amount < 0) or white (amount > 0). */
export function shade(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  return toHex(rgb.map((v) => (amount < 0 ? v * (1 + amount) : v + (255 - v) * amount)));
}

/** Applies the brand to the page: title, browser icon and the brand colour variables. */
export function BrandEffects() {
  const brand = useBrand();
  useEffect(() => {
    document.title = brand.name;
    const root = document.documentElement.style;
    if (brand.partner && brand.color) {
      root.setProperty("--color-brand", brand.color);
      root.setProperty("--color-brand-strong", shade(brand.color, -0.15));
      root.setProperty("--color-brand-soft", shade(brand.color, 0.9));
      root.setProperty("--color-brand-logo", brand.color);
      root.setProperty("--color-accent", shade(brand.color, -0.35));
    } else {
      for (const v of ["--color-brand", "--color-brand-strong", "--color-brand-soft", "--color-brand-logo", "--color-accent"]) root.removeProperty(v);
    }
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icon) icon.href = brand.icon_url ?? "/favicon.png";
  }, [brand.name, brand.partner, brand.color, brand.icon_url]);
  return null;
}

/** The brand mark: a partner's logo or icon, or ours. */
export function BrandMark({ className = "h-8 w-8", onDark = false }: { className?: string; onDark?: boolean }) {
  const brand = useBrand();
  if (brand.icon) return <img src={brand.icon} alt="" className={`${className} ${onDark ? "rounded-xl bg-white/95 p-1" : ""}`} />;
  return (
    <span aria-hidden className={`${className} inline-flex items-center justify-center rounded-lg bg-brand font-semibold text-white`}>
      {brand.name.slice(0, 1)}
    </span>
  );
}
